/**
 * HOD Team Management Component
 *
 * Allows HODs to manage their team members within their category
 * Features worker supervision, task assignment, and team analytics
 */

import React, { useState, useEffect, useCallback } from "react";
import Layout from "../common/Layout";
import { useAuth } from "../../context/AuthContext";
import { getSubcategories } from "../../utils/maintenance-categories";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Box,
  IconButton,
  Avatar,
  Tooltip,
  Badge,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
} from "@mui/material";
import {
  Assignment,
  Visibility,
  Refresh,
  Phone,
  Email,
  CheckCircle,
  PersonAdd,
} from "@mui/icons-material";

const HODTeamManagement = () => {
  const { currentUser } = useAuth();
  const [workers, setWorkers] = useState([]);
  const [hodProfile, setHodProfile] = useState(null);
  const [selectedWorker, setSelectedWorker] = useState(null);
  const [workerDialog, setWorkerDialog] = useState(false);
  const [addWorkerDialog, setAddWorkerDialog] = useState(false);
  const [newWorkerData, setNewWorkerData] = useState({
    name: "",
    email: "",
    phone: "",
    specializations: [],
    experienceYears: "",
    qualifications: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const loadTeamData = useCallback(async () => {
    try {
      setLoading(true);
      const token = await currentUser.getIdToken();

      // Get HOD profile
      const hodResponse = await fetch("/api/v1/hods/me", {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!hodResponse.ok) {
        throw new Error("Failed to get HOD profile");
      }

      const currentHodProfile = await hodResponse.json();
      setHodProfile(currentHodProfile);

      // Get team members
      const teamResponse = await fetch("/api/v1/hods/me/dashboard", {
        headers: { Authorization: `Bearer ${token}` },
      });
      const teamData = await teamResponse.json();

      if (!teamResponse.ok) {
        throw new Error(teamData.message || "Failed to load team data");
      }

      setWorkers(teamData.workers || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    loadTeamData();
  }, [loadTeamData]);

  const handleViewWorker = (worker) => {
    setSelectedWorker(worker);
    setWorkerDialog(true);
  };

  const handleCloseDialog = () => {
    setWorkerDialog(false);
    setSelectedWorker(null);
  };

  const handleAddWorker = async () => {
    try {
      setLoading(true);
      const token = await currentUser.getIdToken();

      const response = await fetch("/api/v1/workers", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          ...newWorkerData,
          category: hodProfile?.category,
          departmentId: hodProfile?.departmentId,
          departmentName: hodProfile?.departmentName,
          specializations: newWorkerData.specializations.filter(
            (s) => s.trim() !== ""
          ),
          experienceYears: parseInt(newWorkerData.experienceYears) || 0,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Failed to create worker");
      }

      setSuccess("Worker created successfully");
      setAddWorkerDialog(false);
      resetAddWorkerForm();
      loadTeamData(); // Reload the team data
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const resetAddWorkerForm = () => {
    setNewWorkerData({
      name: "",
      email: "",
      phone: "",
      specializations: [],
      experienceYears: "",
      qualifications: "",
    });
  };

  const handleCloseAddWorkerDialog = () => {
    setAddWorkerDialog(false);
    resetAddWorkerForm();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "active":
        return "success";
      case "busy":
        return "warning";
      case "inactive":
        return "default";
      default:
        return "default";
    }
  };

  const getWorkloadColor = (taskCount) => {
    if (taskCount === 0) return "success";
    if (taskCount <= 3) return "info";
    if (taskCount <= 6) return "warning";
    return "error";
  };

  if (loading && !workers.length) {
    return <Layout title="Team Management">Loading team data...</Layout>;
  }

  return (
    <Layout title="Team Management">
      <Box sx={{ width: "100%" }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert
            severity="success"
            sx={{ mb: 2 }}
            onClose={() => setSuccess("")}
          >
            {success}
          </Alert>
        )}

        {/* Header Section */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Box>
                <Typography variant="h5" gutterBottom>
                  {hodProfile?.category} Department Team
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Manage your team members and their task assignments
                </Typography>
              </Box>
              <Box sx={{ display: "flex", gap: 2 }}>
                <Button
                  startIcon={<PersonAdd />}
                  onClick={() => setAddWorkerDialog(true)}
                  disabled={loading}
                  variant="contained"
                  color="primary"
                >
                  Add Worker
                </Button>
                <Button
                  startIcon={<Refresh />}
                  onClick={loadTeamData}
                  disabled={loading}
                  variant="outlined"
                >
                  Refresh
                </Button>
              </Box>
            </Box>

            {/* Team Summary Cards */}
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <Box
                  sx={{
                    p: 2,
                    bgcolor: "primary.main",
                    color: "white",
                    borderRadius: 2,
                    textAlign: "center",
                  }}
                >
                  <Typography variant="h4">{workers.length}</Typography>
                  <Typography variant="body2">Total Workers</Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box
                  sx={{
                    p: 2,
                    bgcolor: "success.main",
                    color: "white",
                    borderRadius: 2,
                    textAlign: "center",
                  }}
                >
                  <Typography variant="h4">
                    {workers.filter((w) => w.status === "active").length}
                  </Typography>
                  <Typography variant="body2">Active Workers</Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box
                  sx={{
                    p: 2,
                    bgcolor: "warning.main",
                    color: "white",
                    borderRadius: 2,
                    textAlign: "center",
                  }}
                >
                  <Typography variant="h4">
                    {workers.reduce(
                      (sum, worker) => sum + (worker.currentTaskCount || 0),
                      0
                    )}
                  </Typography>
                  <Typography variant="body2">Total Active Tasks</Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box
                  sx={{
                    p: 2,
                    bgcolor: "info.main",
                    color: "white",
                    borderRadius: 2,
                    textAlign: "center",
                  }}
                >
                  <Typography variant="h4">
                    {workers.reduce(
                      (sum, worker) => sum + (worker.completedTasks || 0),
                      0
                    )}
                  </Typography>
                  <Typography variant="body2">Completed Tasks</Typography>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Team Members Table */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Team Members
            </Typography>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Worker</TableCell>
                    <TableCell>Contact</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Active Tasks</TableCell>
                    <TableCell>Specializations</TableCell>
                    <TableCell>Performance</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {workers.map((worker) => (
                    <TableRow key={worker.id}>
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <Avatar sx={{ mr: 2, bgcolor: "primary.main" }}>
                            {worker.name?.charAt(0)?.toUpperCase()}
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle2">
                              {worker.name}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              ID: {worker.id?.substring(0, 8)}...
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              mb: 0.5,
                            }}
                          >
                            <Email
                              sx={{
                                fontSize: 16,
                                mr: 1,
                                color: "text.secondary",
                              }}
                            />
                            <Typography variant="caption">
                              {worker.email}
                            </Typography>
                          </Box>
                          {worker.phone && (
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                              <Phone
                                sx={{
                                  fontSize: 16,
                                  mr: 1,
                                  color: "text.secondary",
                                }}
                              />
                              <Typography variant="caption">
                                {worker.phone}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={worker.status || "active"}
                          color={getStatusColor(worker.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Badge
                          badgeContent={worker.currentTaskCount || 0}
                          color={getWorkloadColor(worker.currentTaskCount || 0)}
                        >
                          <Assignment />
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Box>
                          {Array.isArray(worker.specializations)
                            ? worker.specializations
                                .slice(0, 2)
                                .map((spec, index) => (
                                  <Chip
                                    key={index}
                                    label={spec}
                                    size="small"
                                    variant="outlined"
                                    sx={{ mr: 0.5, mb: 0.5 }}
                                  />
                                ))
                            : worker.specializations && (
                                <Chip
                                  label={worker.specializations}
                                  size="small"
                                  variant="outlined"
                                />
                              )}
                          {Array.isArray(worker.specializations) &&
                            worker.specializations.length > 2 && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                +{worker.specializations.length - 2} more
                              </Typography>
                            )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <CheckCircle
                            color="success"
                            sx={{ fontSize: 16, mr: 1 }}
                          />
                          <Typography variant="caption">
                            {worker.completedTasks || 0} completed
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleViewWorker(worker)}
                          >
                            <Visibility />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                  {workers.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        <Typography variant="body2" color="text.secondary">
                          No team members found for your category
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>

        {/* Add Worker Dialog */}
        <Dialog
          open={addWorkerDialog}
          onClose={handleCloseAddWorkerDialog}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Add New Worker</DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Full Name"
                    value={newWorkerData.name}
                    onChange={(e) =>
                      setNewWorkerData({
                        ...newWorkerData,
                        name: e.target.value,
                      })
                    }
                    sx={{ mb: 2 }}
                    required
                  />
                  <TextField
                    fullWidth
                    label="Email"
                    type="email"
                    value={newWorkerData.email}
                    onChange={(e) =>
                      setNewWorkerData({
                        ...newWorkerData,
                        email: e.target.value,
                      })
                    }
                    sx={{ mb: 2 }}
                    required
                  />
                  <TextField
                    fullWidth
                    label="Phone Number"
                    value={newWorkerData.phone}
                    onChange={(e) =>
                      setNewWorkerData({
                        ...newWorkerData,
                        phone: e.target.value,
                      })
                    }
                    sx={{ mb: 2 }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Years of Experience"
                    type="number"
                    value={newWorkerData.experienceYears}
                    onChange={(e) =>
                      setNewWorkerData({
                        ...newWorkerData,
                        experienceYears: e.target.value,
                      })
                    }
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    fullWidth
                    label="Qualifications"
                    multiline
                    rows={2}
                    value={newWorkerData.qualifications}
                    onChange={(e) =>
                      setNewWorkerData({
                        ...newWorkerData,
                        qualifications: e.target.value,
                      })
                    }
                    sx={{ mb: 2 }}
                  />
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Specializations</InputLabel>
                    <Select
                      multiple
                      value={newWorkerData.specializations}
                      onChange={(e) =>
                        setNewWorkerData({
                          ...newWorkerData,
                          specializations: e.target.value,
                        })
                      }
                      label="Specializations"
                      renderValue={(selected) => selected.join(", ")}
                    >
                      {hodProfile?.category &&
                        getSubcategories(hodProfile.category).map(
                          (specialization) => (
                            <MenuItem
                              key={specialization}
                              value={specialization}
                            >
                              <Checkbox
                                checked={
                                  newWorkerData.specializations.indexOf(
                                    specialization
                                  ) > -1
                                }
                              />
                              <ListItemText primary={specialization} />
                            </MenuItem>
                          )
                        )}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <Alert severity="info" sx={{ mt: 2 }}>
                    <Typography variant="body2">
                      <strong>Category:</strong> {hodProfile?.category}
                      <br />
                      <strong>Department:</strong>{" "}
                      {hodProfile?.departmentName || "Not specified"}
                      <br />
                      The worker will be automatically assigned to your category
                      and department.
                    </Typography>
                  </Alert>
                </Grid>
              </Grid>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseAddWorkerDialog}>Cancel</Button>
            <Button
              onClick={handleAddWorker}
              disabled={!newWorkerData.name || !newWorkerData.email || loading}
              variant="contained"
            >
              {loading ? "Creating..." : "Create Worker"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Worker Details Dialog */}
        <Dialog
          open={workerDialog}
          onClose={handleCloseDialog}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Avatar sx={{ mr: 2, bgcolor: "primary.main" }}>
                {selectedWorker?.name?.charAt(0)?.toUpperCase()}
              </Avatar>
              <Box>
                <Typography variant="h6">{selectedWorker?.name}</Typography>
                <Typography variant="caption" color="text.secondary">
                  Worker Profile
                </Typography>
              </Box>
            </Box>
          </DialogTitle>
          <DialogContent>
            {selectedWorker && (
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Contact Information
                      </Typography>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                          Email
                        </Typography>
                        <Typography variant="body1">
                          {selectedWorker.email}
                        </Typography>
                      </Box>
                      {selectedWorker.phone && (
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="body2" color="text.secondary">
                            Phone
                          </Typography>
                          <Typography variant="body1">
                            {selectedWorker.phone}
                          </Typography>
                        </Box>
                      )}
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                          Status
                        </Typography>
                        <Chip
                          label={selectedWorker.status || "active"}
                          color={getStatusColor(selectedWorker.status)}
                          size="small"
                        />
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Work Details
                      </Typography>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                          Category
                        </Typography>
                        <Typography variant="body1">
                          {selectedWorker.category}
                        </Typography>
                      </Box>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                          Active Tasks
                        </Typography>
                        <Typography variant="body1">
                          {selectedWorker.currentTaskCount || 0}
                        </Typography>
                      </Box>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                          Completed Tasks
                        </Typography>
                        <Typography variant="body1">
                          {selectedWorker.completedTasks || 0}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Specializations
                      </Typography>
                      <Box>
                        {Array.isArray(selectedWorker.specializations)
                          ? selectedWorker.specializations.map(
                              (spec, index) => (
                                <Chip
                                  key={index}
                                  label={spec}
                                  variant="outlined"
                                  sx={{ mr: 1, mb: 1 }}
                                />
                              )
                            )
                          : selectedWorker.specializations && (
                              <Chip
                                label={selectedWorker.specializations}
                                variant="outlined"
                              />
                            )}
                        {!selectedWorker.specializations && (
                          <Typography variant="body2" color="text.secondary">
                            No specializations specified
                          </Typography>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Close</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Layout>
  );
};

export default HODTeamManagement;
